import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useToast } from '../context/ToastContext';
import axios from 'axios';

const PaymentCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing payment response...');
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [gatewayOrderId, setGatewayOrderId] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    const processPaymentResponse = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const params = Object.fromEntries(urlParams.entries());
        console.log('--- PAYMENT GATEWAY RESPONSE ---');
        console.log('Full URL Parameters:', params);
        
        let decodedStatus = urlParams.get('status');
        let decodedOrderId = urlParams.get('order_id') || urlParams.get('orderNo');
        let decodedTrackingId = urlParams.get('tracking_id') || urlParams.get('trackingId');
        let decodedAmount = urlParams.get('amount') || 'N/A';
        let paymentMode = 'N/A';
        let bankRefNo = 'N/A';
        let currentMessage = urlParams.get('message') || 'No payment response received. Please try again.';

        // Check for CCAvenue encrypted response
        const encResp = urlParams.get('encResp') || (location.state && location.state.encResp);

        if (encResp) {
          console.log('Found encrypted response, decrypting...');
          try {
            const token = localStorage.getItem('token');
            const decryptRes = await axios.post(
              `${process.env.REACT_APP_API_URL}api/ccavenue/response-handler`,
              { encResp: encResp },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (decryptRes.data.status === 'success' && decryptRes.data.data) {
              const data = decryptRes.data.data;
              console.log('Decrypted CCAvenue Data:', data);
              
              decodedStatus = data.order_status === 'Success' ? 'success' : 'error';
              decodedOrderId = data.order_id;
              decodedTrackingId = data.tracking_id;
              decodedAmount = data.amount;
              paymentMode = data.payment_mode || 'N/A';
              bankRefNo = data.bank_ref_no || 'N/A';
              
              if (data.order_status !== 'Success') {
                currentMessage = data.status_message || 'Payment was not successful.';
              }
            }
          } catch (err) {
            console.error('Decryption failed:', err);
            setStatus('error');
            setMessage('Failed to verify payment response.');
            return;
          }
        }

        if (decodedStatus === 'success' && decodedTrackingId) {
          console.log('Payment Status: SUCCESS');
          console.log('Tracking ID:', decodedTrackingId);
          
          setGatewayOrderId(decodedTrackingId);
          setStatus('success');
          setMessage('Payment completed successfully!');
          
          const processedResponse = {
            success: true,
            trackingId: decodedTrackingId,
            orderId: decodedOrderId,
            transactionID: decodedTrackingId, // Mapping as transactionID for display
            status: decodedStatus,
            amount: decodedAmount,
            paymentMethod: paymentMode,
            bankReference: bankRefNo
          };
          
          console.log('Processed Payment Details:', processedResponse);
          setTransactionDetails(processedResponse);
          
          sessionStorage.setItem('paymentTransaction', JSON.stringify(processedResponse));
          
          const pendingData = sessionStorage.getItem('pendingApplication');
          if (pendingData) {
            console.log('Found pending application. Submitting...');
            await submitPendingApplication();
            sessionStorage.removeItem('pendingApplication');
          }

          console.log('Recording student payment record...');
          try {
            await recordStudentPayment(decodedOrderId, decodedTrackingId);
          } catch (e) {
            console.error('Handled recording error:', e);
          }
          
          setMessage('Payment verified! Redirecting to your application...');
          sessionStorage.removeItem('pendingApplication');
          
          setTimeout(() => {
            navigate('/payment');
          }, 1500);
        } else {
          console.log('Payment Status: FAILED or INCOMPLETE');
          setStatus('error');
          setMessage(currentMessage);
          sessionStorage.removeItem('pendingApplication');
        }
      } catch (error) {
        console.error('Payment callback error:', error);
        setStatus('error');
        setMessage('An error occurred while processing payment');
        sessionStorage.removeItem('pendingApplication');
      }
    };

    processPaymentResponse();
  }, [location, navigate]);

  const recordStudentPayment = async (orderId, trackingId) => {
    try {
      const pendingPaymentInfo = sessionStorage.getItem('pendingPaymentInfo');
      if (!pendingPaymentInfo) {
        console.error('No pending payment info found');
        return;
      }

      const info = JSON.parse(pendingPaymentInfo);
      const token = localStorage.getItem('token');

      // 1. Check Capacity first (as requested)
      console.log('Checking capacity before recording payment at:', `${process.env.REACT_APP_API_URL}api/check-capacity`);
      try {
        const capacityResponse = await fetch(`${process.env.REACT_APP_API_URL}api/check-capacity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            session_id: info.session_id,
            category_id: info.category_id
          })
        });

        if (capacityResponse.ok) {
          const capResult = await capacityResponse.json();
          console.log('Capacity check result:', capResult);
          
          if (capResult.status === 'error' || capResult.remaining_capacity <= 0) {
            console.warn('Capacity full or error during final check, but payment was already successful.');
          }
        } else {
          console.warn('Capacity check API failed:', capacityResponse.status);
        }
      } catch (capError) {
        console.error('Error during capacity check:', capError);
      }

      // 2. Hit the appropriate API based on application type
      const finalPayload = {
        session_id: parseInt(info.session_id),
        category_id: parseInt(info.category_id),
        shift_id: parseInt(info.shift_id || 1),
        time_slot_id: parseInt(info.time_slot_id || 1),
        amount: parseFloat(info.amount || 0).toFixed(2),
        payment_id: trackingId || orderId // original tracking/transaction id
      };

      if (info.application_id) {
        finalPayload.application_id = info.application_id;
      }

      console.log('Recording student payment with payload:', finalPayload);

      // Choose API endpoint based on whether it's a new application or renewal
      const apiUrl = info.isNew === false 
        ? `${process.env.REACT_APP_API_URL}api/student-renewal`
        : `${process.env.REACT_APP_API_URL}api/student-payment`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(finalPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(`${info.isNew === false ? 'Student renewal' : 'Student payment'} record result:`, result);
      
      sessionStorage.removeItem('pendingPaymentInfo');
    } catch (error) {
      console.error('Error recording student payment:', error);
    }
  };

  const submitPendingApplication = async () => {
    try {
      const pendingData = sessionStorage.getItem('pendingApplication');
      if (!pendingData) {
        console.error('No pending application data found');
        return;
      }

      const token = localStorage.getItem('token');
      const parsedData = JSON.parse(pendingData);
      
      // Create FormData for submission
      const formData = new FormData();
      
      // Add all form fields
      Object.entries(parsedData).forEach(([key, value]) => {
        formData.set(key, value);
      });
      
      // Add payment status
      formData.set('payment_status', 'success');
      formData.set('payment_type', 'online');
      
      // Check if there were files uploaded originally
      const fileData = sessionStorage.getItem('pendingApplicationFiles');
      
      console.log('Submitting application to swimming-registration API...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/swimming-registration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        console.log('Application submitted successfully after payment');
        
        // Check if files were missing and inform user
        if (fileData) {
          showToast('Application submitted successfully! Note: Due to browser limitations, you may need to re-upload your documents. Please contact support if needed.', 'success');
        } else {
          showToast('Application submitted successfully!', 'success');
        }
      } else {
        const errorMessage = result.message || 'Application submission failed';
        showToast(errorMessage, 'error');
        throw new Error(errorMessage);
      }
      
      // Clean up session storage
      sessionStorage.removeItem('pendingApplication');
      sessionStorage.removeItem('pendingApplicationFiles');
      
    } catch (error) {
      console.error('Application submission error:', error);
      // Removed alert as showToast is now used inside the conditional above or here
      if (!error.message.includes('submission failed')) {
         showToast(error.message, 'error');
      }
      throw error;
    }
  };

  const submitApplication = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/swimming-registration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status !== 'success') {
        throw new Error('Application submission failed');
      }
    } catch (error) {
      console.error('Application submission error:', error);
      throw error;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle size={48} className="text-success" />;
      case 'failure':
        return <XCircle size={48} className="text-danger" />;
      case 'error':
        return <AlertCircle size={48} className="text-warning" />;
      default:
        return <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Processing...</span>
        </div>;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-success';
      case 'failure':
        return 'text-danger';
      case 'error':
        return 'text-warning';
      default:
        return 'text-primary';
    }
  };

  return (
    <div className="dashboard-container">
      <Header />



      <div className="dashboard-wrapper">
        <Sidebar activeItem="application" />
        <div className="flex-grow-1 dashboard-content">
          <div className="payment-callback-container">
            <div className="payment-callback-card">
              <div className="text-center mb-4">
                {getStatusIcon()}
              </div>
              
              <h2 className={`text-center mb-3 ${getStatusColor()}`}>
                {status === 'processing' ? 'Processing Payment' : 
                 status === 'success' ? 'Payment Successful' :
                 status === 'failure' ? 'Payment Failed' : 
                 'Payment Error'}
              </h2>
              
              <p className="text-center text-muted mb-4">
                {message}
              </p>

              {transactionDetails && (
                <div className="transaction-details">
                  <h5>Transaction Details</h5>
                  <div className="detail-item">
                    <span className="label">Transaction ID:</span>
                    <span className="value">{transactionDetails.transactionID || transactionDetails.trackingId || 'N/A'}</span>
                  </div>
                  {transactionDetails.orderId && (
                    <div className="detail-item">
                      <span className="label">Order ID:</span>
                      <span className="value">{transactionDetails.orderId}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="label">Status:</span>
                    <span className="value text-capitalize">{transactionDetails.status}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Amount:</span>
                    <span className="value">₹{transactionDetails.amount}</span>
                  </div>
                  {transactionDetails.paymentMethod && (
                    <div className="detail-item">
                      <span className="label">Payment Method:</span>
                      <span className="value">{transactionDetails.paymentMethod}</span>
                    </div>
                  )}
                  {transactionDetails.bankReference && (
                    <div className="detail-item">
                      <span className="label">Bank Reference:</span>
                      <span className="value">{transactionDetails.bankReference}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="text-center mt-4 d-flex justify-content-center">
                <button 
                  className="enhanced-btn btn-primary"
                  onClick={() => navigate('/payment')}
                >
                  Back to Payment History
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
